import { useEffect, useState, useCallback, useMemo } from 'react'
import { T } from '../lib/tokens'
import { CatDot, FavoriteStar } from '../components/ui'
import { useCategories } from '../lib/hooks'
import { fetchNewsSources, newsKeep, newsFavorite } from '../lib/api'
import { supabase } from '../lib/supabase'

interface NewsArticle {
  id: string
  title: string | null
  url: string
  summary: string | null
  cat_id: string | null
  source_id: string | null
  is_detailed: boolean
  detail_status: string | null
  is_favorite: boolean
  read_later: boolean
  rating: number | null
  item_id: string | null
  posted_at: string | null
  trashed_at: string | null
}

const SELECT_COLS =
  'id,title,url,summary,cat_id,source_id,is_detailed,detail_status,is_favorite,read_later,rating,item_id,posted_at,trashed_at'

// 評価フィルタ（排他: 完全一致チップ ⇔ 「★N以上」星ウィジェットのどちらか一方が有効）
type RatingFilter =
  | { type: 'all' }                   // すべて
  | { type: 'exact'; n: number }      // ちょうど★N
  | { type: 'none' }                  // 未評価（rating=null）
  | { type: 'min'; n: number }        // ★N以上

function hostnameOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, '') } catch { return url }
}

// ── 配信バッチのグルーピング ─────────────────────────────
//
// 1回の配信は TOC + 記事を 400ms 間隔で連投するので、同じバッチ内の posted_at は
// 数十秒しか離れない。一方、別の配信（定時 5:00 と手動トリガーなど）は最低でも
// 数十分〜数時間あく。そこで「間隔が空いたところ」をバッチ境界とみなす。
//
// 日付だけで切ると、同じ日に手動配信すると定時分と混ざって
// 「どこからどこまでが何時の配信か」が分からなくなる。
const DELIVERY_GAP_MS = 30 * 60 * 1000   // 30分

const JST = 'Asia/Tokyo'
const fmtDate = new Intl.DateTimeFormat('ja-JP', { timeZone: JST, month: 'long', day: 'numeric', weekday: 'short' })
const fmtTime = new Intl.DateTimeFormat('ja-JP', { timeZone: JST, hour: '2-digit', minute: '2-digit', hour12: false })
// 'YYYY-MM-DD'（JST）。今日/昨日の判定に使う。
const fmtDayKey = new Intl.DateTimeFormat('en-CA', { timeZone: JST, year: 'numeric', month: '2-digit', day: '2-digit' })

export interface DeliveryGroup {
  key: string
  /** 例: '7月29日(水)'。posted_at が無いグループは null */
  date: string | null
  /** 例: '05:01'。posted_at が無いグループは null */
  time: string | null
  /** 'YYYY-MM-DD'（JST）。posted_at が無いグループは null */
  dayKey: string | null
  articles: NewsArticle[]
}

/**
 * posted_at 降順で並んだ記事を配信バッチ単位に切る。
 * 見出しの時刻は「そのバッチで最初に配信された記事」＝グループ末尾の posted_at を使う
 * （降順なので末尾が最古）。先頭を使うと連投の最後の記事の時刻になり、実際の配信開始より遅く出る。
 *
 * posted_at が null の記事（Discord 配信前・手動投入など）は末尾に1つのグループでまとめる。
 * 時系列に混ぜると境界判定が壊れるため。
 */
export function groupByDelivery(articles: NewsArticle[]): DeliveryGroup[] {
  const groups: DeliveryGroup[] = []
  const undated: NewsArticle[] = []
  let cur: NewsArticle[] = []
  let prevMs: number | null = null

  const flush = () => {
    if (cur.length === 0) return
    const oldest = cur[cur.length - 1]
    const d = new Date(oldest.posted_at as string)
    groups.push({
      key: oldest.id,
      date: fmtDate.format(d),
      time: fmtTime.format(d),
      dayKey: fmtDayKey.format(d),
      articles: cur,
    })
    cur = []
  }

  for (const a of articles) {
    if (!a.posted_at) { undated.push(a); continue }
    const ms = Date.parse(a.posted_at)
    if (Number.isNaN(ms)) { undated.push(a); continue }
    // 降順なので prev の方が新しい。差が閾値を超えたら別の配信とみなす。
    if (prevMs !== null && prevMs - ms > DELIVERY_GAP_MS) flush()
    cur.push(a)
    prevMs = ms
  }
  flush()

  if (undated.length > 0) {
    groups.push({ key: 'undated', date: null, time: null, dayKey: null, articles: undated })
  }
  return groups
}

/** 見出しに添える「今日」「昨日」。それ以外は null。 */
function relativeDayLabel(dayKey: string | null): string | null {
  if (!dayKey) return null
  const now = Date.now()
  if (dayKey === fmtDayKey.format(new Date(now))) return '今日'
  if (dayKey === fmtDayKey.format(new Date(now - 86400000))) return '昨日'
  return null
}

// ニュース「一覧」タブの中身（Sidebar/Topbar は親シェルが描画）。
export function NewsFeedPanel() {
  const { data: categories = [] } = useCategories()
  const [articles, setArticles] = useState<NewsArticle[]>([])
  const [sources, setSources] = useState<Record<string, string | null>>({})
  const [cat, setCat] = useState<string | null>(null)
  const [rating, setRating] = useState<RatingFilter>({ type: 'all' })
  const [showTrashed, setShowTrashed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<Record<string, 'keep' | 'restore' | 'fav'>>({})
  const [msg, setMsg] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('ml_news_articles').select(SELECT_COLS)
      .order('posted_at', { ascending: false }).limit(150)
    q = showTrashed ? q.not('trashed_at', 'is', null) : q.is('trashed_at', null)
    if (cat) q = q.eq('cat_id', cat)
    if (rating.type === 'exact')     q = q.eq('rating', rating.n)
    else if (rating.type === 'none') q = q.is('rating', null)
    else if (rating.type === 'min')  q = q.gte('rating', rating.n)
    const { data } = await q
    setArticles((data ?? []) as NewsArticle[])
    setLoading(false)
  }, [cat, rating, showTrashed])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    fetchNewsSources().then(r => {
      const m: Record<string, string | null> = {}
      for (const s of r.sources ?? []) m[s.id] = s.site_name
      setSources(m)
    }).catch(() => {})
  }, [])

  function sourceLabel(a: NewsArticle): string {
    const name = a.source_id ? sources[a.source_id] : null
    return (name && name.trim()) || hostnameOf(a.url)
  }
  function patch(id: string, p: Partial<NewsArticle>) {
    setArticles(list => list.map(a => a.id === id ? { ...a, ...p } : a))
  }

  async function handleKeep(a: NewsArticle) {
    setBusy(b => ({ ...b, [a.id]: 'keep' }))
    try {
      const r = await newsKeep(a.id)
      if (r.ok) { patch(a.id, { item_id: r.item_id }); setMsg(m => ({ ...m, [a.id]: '📥 保存しました（詳細ページで深掘りできます）' })) }
      else setMsg(m => ({ ...m, [a.id]: '保存に失敗しました' }))
    } catch { setMsg(m => ({ ...m, [a.id]: '保存に失敗しました' })) }
    finally { setBusy(b => { const n = { ...b }; delete n[a.id]; return n }) }
  }

  // ★は Worker 経由。ロッキオ学習・メモへの同期・お気に入り時の自動保存が
  // まとめて走るので、supabase を直接更新してはいけない。
  async function handleFavorite(a: NewsArticle) {
    setBusy(b => ({ ...b, [a.id]: 'fav' }))
    try {
      const r = await newsFavorite(a.id)
      if (r.ok) {
        patch(a.id, { is_favorite: r.is_favorite })
        // ON にすると Worker 側でメモ保存も走る（Discord と同じ挙動）
        if (r.is_favorite) setMsg(m => ({ ...m, [a.id]: '⭐ お気に入りに追加しました（メモにも保存されます）' }))
      } else setMsg(m => ({ ...m, [a.id]: 'お気に入りの更新に失敗しました' }))
    } catch { setMsg(m => ({ ...m, [a.id]: 'お気に入りの更新に失敗しました' })) }
    finally { setBusy(b => { const n = { ...b }; delete n[a.id]; return n }) }
  }

  async function handleRestore(a: NewsArticle) {
    setBusy(b => ({ ...b, [a.id]: 'restore' }))
    try {
      await supabase.from('ml_news_articles').update({ trashed_at: null }).eq('id', a.id)
      setArticles(list => list.filter(x => x.id !== a.id))
    } finally { setBusy(b => { const n = { ...b }; delete n[a.id]; return n }) }
  }

  const catLabel = (id: string | null) => categories.find(c => c.id === id)?.label ?? id ?? '—'

  // 配信バッチ単位に切る。フィルタ後の articles を渡すので、見出しの件数は
  // 「いま画面に見えている件数」になる（配信時の総数ではない）。
  const groups = useMemo(() => groupByDelivery(articles), [articles])

  return (
    <>
      <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:14 }}>
        <span style={{ fontSize:12,color:T.textMute }}>{articles.length}件</span>
        <button onClick={() => setShowTrashed(s => !s)}
          style={{ marginLeft:'auto',padding:'5px 12px',borderRadius:6,border:`1px solid ${T.border}`,background:showTrashed?T.bgActive:'transparent',color:T.textDim,fontSize:11,cursor:'pointer',fontFamily:T.font }}>
          {showTrashed ? '← 一覧に戻る' : '🗑 ゴミ箱'}
        </button>
      </div>

      {/* カテゴリフィルタ */}
      <div style={{ display:'flex',flexWrap:'wrap',gap:6,marginBottom:8 }}>
        <Chip active={cat===null} onClick={() => setCat(null)} label="すべて" />
        {categories.map(c => (
          <Chip key={c.id} active={cat===c.id} onClick={() => setCat(c.id)}
            label={c.label} dot={<CatDot catId={c.id} size={7} />} />
        ))}
      </div>

      {/* 評価フィルタ（完全一致チップ ＋ 「★N以上」星ウィジェット・排他） */}
      <div style={{ display:'flex',flexWrap:'wrap',alignItems:'center',gap:6,marginBottom:18 }}>
        <span style={{ fontSize:10,color:T.textMute,fontFamily:T.fontMono,marginRight:2 }}>評価</span>
        <Chip active={rating.type==='all'} onClick={() => setRating({ type:'all' })} label="すべて" />
        {[5,4,3,2,1].map(n => (
          <Chip key={n} active={rating.type==='exact' && rating.n===n} onClick={() => setRating({ type:'exact', n })} label={`★${n}`} />
        ))}
        <Chip active={rating.type==='none'} onClick={() => setRating({ type:'none' })} label="未評価" />
        <span style={{ fontSize:10,color:T.textMute,marginLeft:8 }}>★以上:</span>
        <StarMin value={rating.type==='min' ? rating.n : 0}
          onPick={n => setRating({ type:'min', n })} onClear={() => setRating({ type:'all' })} />
      </div>

      {loading ? (
        <div style={{ fontSize:12,color:T.textMute }}>読み込み中…</div>
      ) : articles.length === 0 ? (
        <div style={{ fontSize:12,color:T.textMute }}>{showTrashed ? 'ゴミ箱は空です' : '記事がありません'}</div>
      ) : (
        <div style={{ display:'flex',flexDirection:'column',gap:26 }}>
          {groups.map(g => (
          <section key={g.key}>
          <DeliveryHeading group={g} />
          <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
          {g.articles.map(a => (
            <div key={a.id} style={{ padding:'10px 12px',borderRadius:8,border:`1px solid ${T.border}`,opacity:a.trashed_at?0.65:1 }}>
              <div style={{ display:'flex',alignItems:'baseline',gap:8,marginBottom:4 }}>
                <span style={{ display:'inline-flex',alignItems:'center',gap:4,fontSize:10,color:T.textMute,flexShrink:0 }}>
                  <CatDot catId={a.cat_id} size={7} />{catLabel(a.cat_id)}
                </span>
                {a.is_detailed && <Badge>📖 深掘り済み</Badge>}
                {a.item_id && <Badge>📥 保存済み</Badge>}
                <FavoriteStar
                  active={a.is_favorite}
                  busy={busy[a.id] === 'fav'}
                  onToggle={() => handleFavorite(a)}
                />
                {a.read_later && <Badge>🔖</Badge>}
                {typeof a.rating === 'number' && <span style={{ fontSize:11,color:T.accent }}>{'★'.repeat(a.rating)}</span>}
              </div>
              <a href={a.url} target="_blank" rel="noreferrer"
                style={{ fontSize:13,fontWeight:600,color:T.text,textDecoration:'none',display:'block',marginBottom:3 }}>
                {a.title || a.url}
              </a>
              {a.summary && <div style={{ fontSize:11,color:T.textDim,lineHeight:1.5,marginBottom:6,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden' }}>{a.summary}</div>}
              <div style={{ display:'flex',alignItems:'center',gap:10,fontSize:11 }}>
                <span style={{ color:T.textMute,fontFamily:T.fontMono }}>📍 {sourceLabel(a)}</span>
                <span style={{ marginLeft:'auto',display:'flex',gap:8 }}>
                  {a.trashed_at ? (
                    <ActBtn onClick={() => handleRestore(a)} disabled={!!busy[a.id]}>{busy[a.id]==='restore'?'…':'♻ 復元'}</ActBtn>
                  ) : (
                    <>
                      {a.item_id
                        ? <ItemLink itemId={a.item_id} />
                        : <ActBtn onClick={() => handleKeep(a)} disabled={!!busy[a.id]}>{busy[a.id]==='keep'?'…':'📥 keep'}</ActBtn>}
                    </>
                  )}
                </span>
              </div>
              {msg[a.id] && <div style={{ fontSize:10,color:T.textDim,marginTop:6 }}>{msg[a.id]}</div>}
            </div>
          ))}
          </div>
          </section>
          ))}
        </div>
      )}
    </>
  )
}

// 配信バッチの区切り見出し。「7月29日(水) 05:01 ・6件 ───────」
function DeliveryHeading({ group }: { group: DeliveryGroup }) {
  const rel = relativeDayLabel(group.dayKey)
  return (
    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:10 }}>
      <span style={{ fontSize:11,color:T.textDim,fontFamily:T.fontMono,whiteSpace:'nowrap' }}>
        {group.date ? `${group.date} ${group.time}` : '配信日時なし'}
      </span>
      {rel && (
        <span style={{ fontSize:9,color:T.textMute,border:`1px solid ${T.border}`,borderRadius:4,padding:'1px 5px',whiteSpace:'nowrap' }}>
          {rel}
        </span>
      )}
      <span style={{ fontSize:10,color:T.textMute,whiteSpace:'nowrap' }}>{group.articles.length}件</span>
      {/* 残り幅いっぱいの区切り線 */}
      <span style={{ flex:1,height:1,background:T.border }} />
    </div>
  )
}

function ItemLink({ itemId }: { itemId: string }) {
  return (
    <a href={`/items/${itemId}`}
      style={{ padding:'3px 10px',borderRadius:6,border:`1px solid ${T.border}`,color:T.textDim,fontSize:11,textDecoration:'none',fontFamily:T.font }}>
      詳細を開く →
    </a>
  )
}

function Chip({ active, onClick, label, dot }: { active: boolean; onClick: () => void; label: string; dot?: React.ReactNode }) {
  return (
    <button onClick={onClick}
      style={{ display:'inline-flex',alignItems:'center',gap:5,padding:'4px 10px',borderRadius:14,border:`1px solid ${active?T.borderHi:T.border}`,background:active?T.bgActive:'transparent',color:active?T.text:T.textDim,fontSize:11,cursor:'pointer',fontFamily:T.font }}>
      {dot}{label}
    </button>
  )
}

// 「★N以上」を選ぶクリック式星ウィジェット。N個目をクリックで rating>=N。✕でクリア。
function StarMin({ value, onPick, onClear }: { value: number; onPick: (n: number) => void; onClear: () => void }) {
  return (
    <span style={{ display:'inline-flex',alignItems:'center',gap:1 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} onClick={() => onPick(n)} title={`★${n}以上`}
          style={{ cursor:'pointer',fontSize:15,lineHeight:1,color: n<=value ? T.accent : T.textMute }}>★</span>
      ))}
      {value > 0 && (
        <button onClick={onClear} title="クリア"
          style={{ marginLeft:4,background:'transparent',border:'none',cursor:'pointer',color:T.textMute,fontSize:11 }}>✕</button>
      )}
    </span>
  )
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize:9,color:T.textDim,border:`1px solid ${T.border}`,borderRadius:4,padding:'1px 5px' }}>{children}</span>
}

function ActBtn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ padding:'3px 10px',borderRadius:6,border:`1px solid ${T.border}`,background:'transparent',color:T.textDim,fontSize:11,cursor:disabled?'default':'pointer',fontFamily:T.font }}>
      {children}
    </button>
  )
}
